import { useEffect, useState, type ReactNode } from "react";
import { Loader2 } from "lucide-react";

import { type Conversation } from "shared/apiClient";
import { getText } from "shared/lib";

import { useCaptureEvent, CaptureEventType } from "@/hooks";

import { Button, Separator } from "shared/components";
import { AnalysisDisclaimerContent } from "./AnalysisDisclaimerContent";

export interface AnalysisDisclaimerPanelProps {
  buttonLabel?: ReactNode;
  conversation: Conversation | undefined | null;
  disclaimers?: ReactNode;
  onClickSubmit: () => void;
  submitting?: boolean;
}

export function AnalysisDisclaimerPanel({
  buttonLabel,
  conversation,
  disclaimers,
  onClickSubmit,
  submitting,
}: AnalysisDisclaimerPanelProps) {
  const { captureEvent } = useCaptureEvent();

  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    captureEvent(CaptureEventType.USER_CONFIRM_REPORT_RENDERED);
  }, []); // eslint-disable-line

  const handleClickShowReport = () => {
    if (accepted) {
      captureEvent(CaptureEventType.USER_CONFIRM_REPORT_ACCEPTED);
      onClickSubmit();
    }
  };

  return (
    <div className="flex flex-col gap-5 rounded-xl p-5 shadow-[0px_0px_1px_0px_rgba(0,0,0,0.20),0px_26px_80px_0px_rgba(0,0,0,0.20)] md:max-w-[624px] md:p-10" data-testid="analysis-disclaimer-panel">
      <div className="flex flex-col gap-1">
        <h1 className="text-headingxl md:text-heading2xl">
          {getText("🥳 Your analysis is almost ready!")}
        </h1>

        <p className="text-textSubdued text-bodylg">
          {getText("Before we generate it, please agree to our end user agreement.")}
        </p>
      </div>

      {disclaimers ? (
        <>
          {disclaimers}
          <Separator orientation="horizontal" />
        </>
      ) : null}

      <AnalysisDisclaimerContent
        conversation={conversation}
        onChangeDisclaimerAcceptance={setAccepted}
      />

      <div className="mt-3 flex justify-end md:mt-8">
        <Button disabled={!accepted} onClick={handleClickShowReport}>
          {submitting ? (
            <>
              <Loader2 className="mr-3 animate-spin" data-testid="submitting" />
            </>
          ) : null}
          {buttonLabel || getText("Generate analysis")}
        </Button>
      </div>
    </div>
  );
}
